import { IconName } from "@zapier/zinnia-icons";
import { Node } from "@xyflow/react";
import { BuildModeSuggestionType } from "./types";

/**
 * Suggestion type that matches ai-toolkit v4's built-in emptyStateSuggestions
 */
export type CopilotSuggestion = {
  displayText: string;
  iconName?: IconName;
  prompt?: string;
};

/**
 * Maps BuildModeSuggestionType enum values to the prompts sent to the LLM
 */
export const promptMapping: Record<BuildModeSuggestionType, string> = {
  [BuildModeSuggestionType.SETUP_ZAPS]:
    "Help me configure field mapping for the Zap workflows on this canvas. Please work through them one at a time and ask for any additional information you need.",
  [BuildModeSuggestionType.LEAD_GENERATION]:
    "Build a lead generation system for me.",
  [BuildModeSuggestionType.CUSTOMER_SUPPORT]:
    "Build a customer support automation for me.",
  [BuildModeSuggestionType.ECOMMERCE_ORDER]:
    "Build an e-commerce order processing system for me.",
  [BuildModeSuggestionType.TOUR_MY_CANVAS]: "Use the tour tool",
  [BuildModeSuggestionType.EXPLAIN_MY_CANVAS]:
    "Explain what I have built on this canvas and how it works.",
  [BuildModeSuggestionType.WHAT_CAN_COPILOT_DO]:
    "What can Canvas Copilot do? Tell me about all the capabilities and features available.",
  [BuildModeSuggestionType.ZAPIER_COMMON_USE_CASES]:
    "What are common use cases for Zapier? Show me some popular automation examples and workflows.",
  [BuildModeSuggestionType.HELP_BUILD_MY_CANVAS]:
    "Help me build automations on this canvas. What should I create?",
  [BuildModeSuggestionType.LEARN_ABOUT_CANVAS]:
    "Teach me about Canvas and how to use it effectively for building automations.",
  [BuildModeSuggestionType.ZAPIER_FOR_BEGINNERS]:
    "I'm new to Zapier. Can you teach me the basics and help me understand how to get started?",
  [BuildModeSuggestionType.CREATE_AUTH]: "Create new App Connection", // This won't be used in empty state
};

/**
 * Maps BuildModeSuggestionType enum values to Zinnia icon names
 */
export const iconMapping: Record<BuildModeSuggestionType, IconName> = {
  [BuildModeSuggestionType.SETUP_ZAPS]: "miscBoltAlt",
  [BuildModeSuggestionType.LEAD_GENERATION]: "miscLearn",
  [BuildModeSuggestionType.CUSTOMER_SUPPORT]: "miscCopilot",
  [BuildModeSuggestionType.ECOMMERCE_ORDER]: "miscLearn",
  [BuildModeSuggestionType.TOUR_MY_CANVAS]: "navCanvas",
  [BuildModeSuggestionType.EXPLAIN_MY_CANVAS]: "alertInfoFill",
  [BuildModeSuggestionType.WHAT_CAN_COPILOT_DO]: "miscCopilot",
  [BuildModeSuggestionType.ZAPIER_COMMON_USE_CASES]: "miscBoltAlt",
  [BuildModeSuggestionType.HELP_BUILD_MY_CANVAS]: "commentConvo",
  [BuildModeSuggestionType.LEARN_ABOUT_CANVAS]: "navCanvas",
  [BuildModeSuggestionType.ZAPIER_FOR_BEGINNERS]: "miscLearn",
  [BuildModeSuggestionType.CREATE_AUTH]: "miscCopilot",
};

/**
 * Converts a BuildModeSuggestionType to a CopilotSuggestion
 */
function buildModeSuggestionToCopilotSuggestion(
  suggestionType: BuildModeSuggestionType,
): CopilotSuggestion {
  return {
    displayText: suggestionType,
    iconName: iconMapping[suggestionType],
    prompt: promptMapping[suggestionType],
  };
}

/**
 * Determines which suggestions to show based on canvas state
 * This replaces the logic from PersistentSuggestionButtons component
 */
export function getCopilotSuggestions({
  isBlankCanvas,
  isDraftModeIncludingTemplates,
  nodes,
}: {
  isBlankCanvas: boolean;
  isDraftModeIncludingTemplates: boolean;
  nodes: Node[];
}): CopilotSuggestion[] {
  // Show 4 default prompts for blank/new canvas (regardless of draft mode)
  if (isBlankCanvas) {
    return [
      BuildModeSuggestionType.LEARN_ABOUT_CANVAS,
      BuildModeSuggestionType.HELP_BUILD_MY_CANVAS,
      BuildModeSuggestionType.WHAT_CAN_COPILOT_DO,
      BuildModeSuggestionType.ZAPIER_FOR_BEGINNERS,
    ].map(buildModeSuggestionToCopilotSuggestion);
  }

  // Show contextual suggestions for canvas with content but not in draft mode
  if (!isDraftModeIncludingTemplates) {
    // Check if there are any zap nodes on the canvas
    const hasZapNodes = nodes.some((node) => node.type === "zap");

    // Base suggestions for any non-blank canvas (always 4 buttons)
    const suggestions = [
      BuildModeSuggestionType.TOUR_MY_CANVAS,
      BuildModeSuggestionType.EXPLAIN_MY_CANVAS,
      BuildModeSuggestionType.WHAT_CAN_COPILOT_DO,
    ];

    // Add Setup My Zaps if there are zap nodes, otherwise add a general help option
    if (hasZapNodes) {
      suggestions.push(BuildModeSuggestionType.SETUP_ZAPS);
    } else {
      suggestions.push(BuildModeSuggestionType.ZAPIER_FOR_BEGINNERS);
    }

    return suggestions.map(buildModeSuggestionToCopilotSuggestion);
  }

  // Default case - no suggestions (draft mode)
  return [];
}
